import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { toast } from "react-toastify";
import {
  Edit,
  ArrowUpDown,
  Search,
  Plus,
  Calendar,
  CheckCircle,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  Package,
  Settings,
  Zap,
  Weight,
  User,
} from "lucide-react";
import { MakeAndModel, Manufacturer } from "../../../types/manufacturer";
import { makeAndModelService, manufacturerService } from "../../../services/manufacturerService";
import { LoadingSpinner } from "../../../shared/LoadingSpinner";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import MakeAndModelForm from "./MakeAndModelForm";
import useDebounce from "../../../hooks/useDebounce";
import ErrorAlert from "../../../shared/components/ui/ErrorAlert";
import Select from "../../../shared/components/form/Select";
import { userService } from "../../../services/userService";

// Modern Clean Brand Colors
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  primaryBorder: "#D1E7DD",
  accent: "#A3CD39",
  accentLight: "#F7FAF0",
  dark: "#007F66",
  neutral: {
    25: "#FCFCFD",
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
    500: "#6B7280",
    600: "#4B5563",
    700: "#374151",
    800: "#1F2937",
    900: "#111827"
  },
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

// Enhanced ToggleStatusCell Component
interface ToggleStatusCellProps {
  makeAndModel: MakeAndModel;
  onRefresh: () => void;
}

const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({ makeAndModel, onRefresh }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const makeAndModelId = makeAndModel._id || makeAndModel.id;
      if (!makeAndModelId) {
        throw new Error("Make and Model ID is required");
      }

      await makeAndModelService.updateMakeAndModel(String(makeAndModelId), {
        status: makeAndModel.status === "active" ? "inactive" : "active"
      });

      toast.success(`Make & Model ${makeAndModel.status === "active" ? "deactivated" : "activated"} successfully`);
      setDrawerOpen(false);
      onRefresh();
    } catch (err: unknown) {
      console.error("Toggle error:", err);
      toast.error("Failed to toggle make & model status");
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = makeAndModel.status === "active";

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setDrawerOpen(true)}
        title={`Click to ${isActive ? "Deactivate" : "Activate"}`}
        className={`relative inline-flex items-center justify-center w-10 h-5 rounded-full border transition-all duration-300 select-none ${isActive
          ? "bg-green-50 border-green-200 hover:bg-green-100"
          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
          }`}
      >
        <div
          className={`w-3 h-3 rounded-full transform transition-all duration-300 ${isActive
            ? "translate-x-2 bg-green-500"
            : "-translate-x-2 bg-gray-400"
            }`}
        />
      </button>

      <RightDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={isActive ? "Deactivate Make & Model" : "Activate Make & Model"}
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleToggle}
              disabled={isLoading}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-all duration-200 disabled:opacity-50 ${isActive
                ? "bg-red-500 hover:bg-red-600 focus:ring-red-500"
                : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                } focus:outline-none focus:ring-2 focus:ring-offset-2`}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span>{isActive ? "Deactivate" : "Activate"}</span>
              )}
            </button>
          </div>
        }
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0 ${isActive ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
            {isActive ? (
              <XCircleIcon size={20} className="text-red-500" />
            ) : (
              <CheckCircle size={20} className="text-green-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 mb-1">{makeAndModel.name}</p>
            <p className="text-sm text-gray-700 mb-3 leading-relaxed">
              Are you sure you want to{" "}
              <span className="font-medium text-gray-900">{isActive ? "deactivate" : "activate"}</span>{" "}
              <span className="font-medium break-words" style={{ color: BRAND_COLORS.primary }}>{makeAndModel.name}</span>?
            </p>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              <p className="text-xs text-gray-600 leading-relaxed">
                {isActive
                  ? "This make & model will no longer be available for selection in equipment."
                  : "This make & model will become available for selection in equipment."}
              </p>
            </div>
          </div>
        </div>
      </RightDrawer>
    </>
  );
};

// Filter Component
type OptionType = { label: string; value: string };

interface MakeAndModelFilterProps {
  status: string | null;
  setStatus: (val: string | null) => void;
  manufacturerId: string | null;
  setManufacturerId: (val: string | null) => void;
  manufacturers: Manufacturer[];
}

const MakeAndModelFilter = ({
  status,
  setStatus,
  manufacturerId,
  setManufacturerId,
  manufacturers
}: MakeAndModelFilterProps) => {

  const manufacturerOptions: OptionType[] = [
    { value: "", label: "All Manufacturers" },
    ...manufacturers.map(m => ({ value: String(m._id || m.id || ''), label: m.name }))
  ];

  const statusOptions: OptionType[] = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" }
  ];

  const handleResetFilters = () => {
    setStatus("");
    setManufacturerId(null);
  };

  const hasActiveFilters = (status && status !== "") || (manufacturerId && manufacturerId !== "");

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
      {/* Manufacturer Filter */}
      <div className="flex-1 min-w-[180px] relative z-10">
        <Select
          options={manufacturerOptions}
          value={manufacturerOptions.find((opt) => opt.value === manufacturerId)}
          placeholder="Filter by Manufacturer"
          onChange={(selectedOption) => {
            setManufacturerId(selectedOption?.value && selectedOption.value !== "" ? selectedOption.value : null);
          }}
        />
      </div>
      {/* Status Filter */}
      <div className="flex-1 min-w-[180px] relative z-10">
        <Select
          options={statusOptions}
          value={statusOptions.find((opt) => opt.value === (status || ""))}
          placeholder="Filter by Status"
          onChange={(selectedOption) => {
            setStatus(selectedOption?.value && selectedOption.value !== "" ? selectedOption.value : null);
          }}
        />
      </div>



      {/* Reset Filters Button */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={handleResetFilters}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
        >
          <X size={14} />
          Clear
        </button>
      )}
    </div>
  );
};

interface Props {
  refreshTrigger: number;
  onRefresh: () => void;
}

const ITEMS_PER_PAGE = 6;
const DEBOUNCE_DELAY = 500;

export default function MakeAndModelTable({ refreshTrigger, onRefresh }: Props) {
  const [makeAndModels, setMakeAndModels] = useState<MakeAndModel[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<MakeAndModel | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [status, setStatus] = useState<string | null>("");
  const [manufacturerId, setManufacturerId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
  const [showFilters] = useState(true);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  // Fetch user names for Created By column
  useEffect(() => {
    const fetchUserNames = async () => {
      const uniqueUserIds = [...new Set(
        makeAndModels
          .map(m => m.createdBy)
          .filter((id): id is string => !!id)
      )];

      const missingIds = uniqueUserIds.filter(id => !userNames[id]);
      if (missingIds.length === 0) return;

      const results = await Promise.all(
        missingIds.map(async (userId) => {
          try {
            const user = await userService.getUserById(userId);
            return { id: userId, name: user.name };
          } catch (error) {
            console.error(`Failed to fetch user ${userId}:`, error);
            return { id: userId, name: 'Unknown User' };
          }
        })
      );

      const newNames: Record<string, string> = {};
      results.forEach(({ id, name }) => { newNames[id] = name; });
      setUserNames(prev => ({ ...prev, ...newNames }));
    };

    if (makeAndModels.length > 0) {
      fetchUserNames();
    }
  }, [makeAndModels]);

  const getUserName = (userId?: string) => {
    if (!userId) return 'System';
    return userNames[userId] || 'Loading...';
  };

  // Load manufacturers for filter
  useEffect(() => {
    const loadManufacturers = async () => {
      try {
        const res = await manufacturerService.getManufacturersForDropdown();
        setManufacturers(res);
      } catch (err) {
        console.error("Failed to load manufacturers:", err);
      }
    };
    loadManufacturers();
  }, []);

  // Handle manufacturer addition
  const handleManufacturerAdded = (newManufacturer: Manufacturer) => {
    console.log('New manufacturer added:', newManufacturer);
    setManufacturers(prev => {
      // Check if manufacturer already exists to prevent duplicates
      const exists = prev.some(m => (m._id || m.id) === (newManufacturer._id || newManufacturer.id));
      if (exists) {
        return prev;
      }
      return [...prev, newManufacturer];
    });
    toast.success("Manufacturer added successfully!");
  };

  const fetchMakeAndModels = useCallback(async (abortController?: AbortController) => {
    console.log('fetchMakeAndModels called with manufacturerId:', manufacturerId);
    setIsLoading(true);
    setError(null);

    try {
      const filters: any = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: sortField,
        sortOrder,
      };

      // Only add filters if they have values
      if (debouncedSearchTerm) filters.search = debouncedSearchTerm;
      filters.status = (status && status !== "") ? status : "all";
      if (manufacturerId) filters.manufacturerId = manufacturerId;

      console.log('Filters being sent:', filters);
      console.log('ManufacturerId:', manufacturerId);

      const res = await makeAndModelService.getMakeAndModels(filters);

      setMakeAndModels(res.data || []);
      setTotalItems(res.meta?.totalDocs || 0);
    } catch (err: any) {
      if (err.name === 'CanceledError') {
        console.log('Request was canceled');
        return;
      }

      setError(err.response?.data?.message || "Failed to fetch models");
      console.error("API Error:", err);
      setMakeAndModels([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, status, manufacturerId, page, sortField, sortOrder]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchMakeAndModels(abortController);

    return () => abortController.abort();
  }, [fetchMakeAndModels]);

  // Refresh when trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchMakeAndModels();
    }
  }, [refreshTrigger, fetchMakeAndModels]);

  const handleAdd = (item: MakeAndModel) => {
    setEditingItem(null);
    setOpenForm(false);
    setPage(1);
    fetchMakeAndModels();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSortOption = (field: string, order: "asc" | "desc" | "clear") => {
    if (order === "clear") {
      setSortField("");
      setSortOrder("asc");
    } else {
      setSortField(field);
      setSortOrder(order);
    }
    setSortMenuOpen(null);
    setPage(1);
  };

  // Function to handle filter changes - triggers parent refetch
  const handleFilterChange = () => {
    setPage(1); // Reset to first page on filter
  };

  // Ctrl+K to focus
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <>
      {error && (
        <ErrorAlert
          message={error}
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-lg border border-gray-200"
      >
        {/* Controls Section */}
        <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left Side: Search */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={16} className="text-gray-400" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search models... (Ctrl+K)"
                  className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                  style={{
                    '--tw-ring-color': BRAND_COLORS.primary,
                    'focusBorderColor': BRAND_COLORS.primary
                  } as React.CSSProperties}
                />
              </div>
            </div>

            {/* Right Side: Sort and Add */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {/* Sort Button */}
                <div className="relative">
                  <button
                    onClick={() => setSortMenuOpen(sortMenuOpen ? null : "general")}
                    className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 transition-all duration-200"
                    style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                  >
                    <ArrowUpDown size={16} />
                    Sort
                  </button>

                  {/* Sort Dropdown */}
                  <AnimatePresence>
                    {sortMenuOpen === "general" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full right-0 mt-2 z-40 w-48 bg-white border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="p-3">
                          <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Name</div>
                          <button
                            onClick={() => handleSortOption("name", "asc")}
                            className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "asc"
                              ? `font-medium bg-opacity-10`
                              : "text-gray-700"
                              }`}
                            style={sortField === "name" && sortOrder === "asc" ? {
                              color: BRAND_COLORS.primary,
                              backgroundColor: BRAND_COLORS.primaryLight
                            } : {}}
                          >
                            A → Z
                          </button>
                          <button
                            onClick={() => handleSortOption("name", "desc")}
                            className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "desc"
                              ? `font-medium bg-opacity-10`
                              : "text-gray-700"
                              }`}
                            style={sortField === "name" && sortOrder === "desc" ? {
                              color: BRAND_COLORS.primary,
                              backgroundColor: BRAND_COLORS.primaryLight
                            } : {}}
                          >
                            Z → A
                          </button>

                          <div className="border-t border-gray-200 my-2"></div>
                          <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Date</div>
                          <button
                            onClick={() => handleSortOption("createdAt", "desc")}
                            className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "desc"
                              ? `font-medium bg-opacity-10`
                              : "text-gray-700"
                              }`}
                            style={sortField === "createdAt" && sortOrder === "desc" ? {
                              color: BRAND_COLORS.primary,
                              backgroundColor: BRAND_COLORS.primaryLight
                            } : {}}
                          >
                            Newest First
                          </button>
                          <button
                            onClick={() => handleSortOption("createdAt", "asc")}
                            className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "asc"
                              ? `font-medium bg-opacity-10`
                              : "text-gray-700"
                              }`}
                            style={sortField === "createdAt" && sortOrder === "asc" ? {
                              color: BRAND_COLORS.primary,
                              backgroundColor: BRAND_COLORS.primaryLight
                            } : {}}
                          >
                            Oldest First
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Add Make & Model Button */}
              <button
                onClick={() => {
                  setEditingItem(null);
                  setOpenForm(true);
                }}
                className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-emerald-200"
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                  '--tw-ring-color': BRAND_COLORS.primary
                } as React.CSSProperties}
              >
                <div className="relative">
                  <Plus size={16} className="absolute animate-ping opacity-40" />
                  <Plus size={16} className="relative" />
                </div>
                <span>Add Make & Model</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-4"
              >
                <MakeAndModelFilter
                  status={status}
                  setStatus={(val) => {
                    setStatus(val);
                    handleFilterChange();
                  }}
                  manufacturerId={manufacturerId}
                  setManufacturerId={(val) => {
                    console.log('Setting manufacturerId to:', val);
                    setManufacturerId(val);
                    handleFilterChange();
                  }}
                  manufacturers={manufacturers}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr style={{ backgroundColor: BRAND_COLORS.neutral[25] }}>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 tracking-wider">
                  Make & Model Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 tracking-wider">
                  Manufacturer
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 tracking-wider">
                  Specifications
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : makeAndModels.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Package size={48} className="text-gray-300" />
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 mb-1">No models found</h3>
                        <p className="text-sm text-gray-500">Get started by creating a new make & model.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                makeAndModels.map((makeAndModel, index) => (
                  <motion.tr
                    key={makeAndModel._id || makeAndModel.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        <div className="ml-0">
                          <div className="text-sm font-semibold text-gray-900">
                            {makeAndModel.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {makeAndModel.modelNumber && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 mr-2">
                                {makeAndModel.modelNumber}
                              </span>
                            )}
                            {makeAndModel.description || "No description"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 font-medium">
                        {(typeof makeAndModel.manufacturerId === 'object' && makeAndModel.manufacturerId?.name) ||
                          (typeof makeAndModel.manufacturerId === 'string' ?
                            manufacturers.find(m => m._id === makeAndModel.manufacturerId)?.name :
                            null) ||
                          "Unknown"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 space-y-1">
                        {makeAndModel.specifications?.power && (
                          <div className="flex items-center gap-1">
                            <Zap size={12} className="text-gray-400" />
                            <span className="text-xs">{makeAndModel.specifications.power}W</span>
                          </div>
                        )}
                        {makeAndModel.specifications?.voltage && (
                          <div className="flex items-center gap-1">
                            <Settings size={12} className="text-gray-400" />
                            <span className="text-xs">{makeAndModel.specifications.voltage}V</span>
                          </div>
                        )}
                        {makeAndModel.specifications?.weight && (
                          <div className="flex items-center gap-1">
                            <Weight size={12} className="text-gray-400" />
                            <span className="text-xs">{makeAndModel.specifications.weight}kg</span>
                          </div>
                        )}
                        {makeAndModel.specifications?.efficiency && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-green-600 font-medium">
                              {makeAndModel.specifications.efficiency}% efficiency
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-1">
                          <User size={14} className="text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {getUserName(makeAndModel.createdBy)}
                          </span>
                        </div>
                        {makeAndModel.createdAt && (
                          <>
                            <span className="text-xs text-gray-500">
                              {new Date(makeAndModel.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(makeAndModel.createdAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-center gap-4">
                        <ToggleStatusCell makeAndModel={makeAndModel} onRefresh={onRefresh} />

                        <button
                          onClick={() => {
                            setEditingItem(makeAndModel);
                            setOpenForm(true);
                          }}
                          title="Edit Make & Model"
                          className={`transition-colors duration-200 ${makeAndModel.status !== "active"
                            ? "text-gray-300 hover:text-gray-400"
                            : "text-gray-400 hover:text-emerald-400"
                            }`}
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-semibold">
                    {Math.min((page - 1) * ITEMS_PER_PAGE + 1, totalItems)}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold">
                    {Math.min(page * ITEMS_PER_PAGE, totalItems)}
                  </span>{" "}
                  of <span className="font-semibold">{totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    ←
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-200 ${pageNum === page
                          ? "z-10 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        style={{
                          backgroundColor: pageNum === page ? BRAND_COLORS.primaryLight : undefined,
                          borderColor: pageNum === page ? BRAND_COLORS.primary : undefined,
                          color: pageNum === page ? BRAND_COLORS.primary : undefined
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    →
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <RightDrawer
        open={openForm}
        title={editingItem ? "Edit Make & Model" : "Add Make & Model"}
        onClose={() => {
          setOpenForm(false);
          setEditingItem(null);
        }}
        size="50"
      >
        <MakeAndModelForm
          editingItem={editingItem}
          onAdd={handleAdd}
          onClose={() => {
            setOpenForm(false);
            setEditingItem(null);
          }}
          manufacturers={manufacturers}
          onManufacturerAdded={handleManufacturerAdded}
        />
      </RightDrawer>
    </>
  );
}
